// client/src/components/pages/Geofencing/GeofenceManager.tsx (converted from .js, ST-00228)
// Zone list + create/edit. With LexiStar versioning (`versioning`, migration
// 021) rows show the purpose type and current version, and each row opens its
// version history; without it the list is exactly as before.
//
// Creating, editing and deactivating a zone needs `geofencing.manage` (audit TRK-10):
// the server refuses it otherwise, so without the key the list is read-only.
//
// ST-00269: the Alerts and Dwell columns show the zone's rules (what the LexiStar engine
// evaluates), not the deprecated Alert on entry / exit and Dwell alert columns; without
// versioning (no rules) they show a dash.
//
// ST-00282: with `alertSwitch` (migration 072) the Alerts column leads with the zone's Alerts
// switch — a geofencing.manage user switches it right there (one save, one new version); a no-go
// zone's is locked on with the reason; read-only users see it without being able to press it.
// While a zone's alerts are off its entry / exit badges are greyed: they apply again when on.
import { useState } from 'react';
import type { ComponentType } from 'react';
import type { AxiosError } from 'axios';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import GeofenceForm, { type GeofencePayload } from './GeofenceForm';
import GeofenceVersionHistoryJs from './GeofenceVersionHistory';
import { ZoneTypeBadge as ZoneTypeBadgeJs, getZoneType as getZoneTypeJs } from './zoneTypes';
import { PurposeBadge as PurposeBadgeJs } from './purposeTypes';
import { nogoAlertsReason, zoneAlertsLocked, zoneAlertsOn, zoneRuleSummary } from './geofenceConfig';
import ZoneAlertsSwitch from './ZoneAlertsSwitch';
import { FaPlus, FaEdit, FaTrash, FaMapMarkerAlt, FaHistory } from 'react-icons/fa';
import RecomputeOfferJs from '../LexiStar/RecomputeJobs/RecomputeOffer'; // M3-U1
import type { GeofenceRow } from '../../../types/clientPages/geofencing';

// Typed views of the JavaScript modules this component uses.
const GeofenceVersionHistory = GeofenceVersionHistoryJs as unknown as ComponentType<{
  geofence: GeofenceRow | null;
  isOpen: boolean;
  onClose: () => void;
}>;
const ZoneTypeBadge = ZoneTypeBadgeJs as unknown as ComponentType<{ type: string }>;
const getZoneType = getZoneTypeJs as unknown as (zone: GeofenceRow) => string;
const PurposeBadge = PurposeBadgeJs as unknown as ComponentType<{
  purpose: string;
  loading?: GeofenceRow['LoadingRole'];
  offloading?: GeofenceRow['OffloadingRole'];
}>;
const RecomputeOffer = RecomputeOfferJs as unknown as ComponentType<{
  objectType: string;
  objects: GeofenceRow[];
  idKey: string;
  versionKey: string;
  nameKey: string;
}>;

export interface GeofenceManagerProps {
  geofences?: GeofenceRow[];
  onChange?: () => void;
  versioning?: boolean;
  versionHistory?: boolean;
  /** ST-00282: the server has the zone Alerts switch (migration 072). */
  alertSwitch?: boolean;
}

const errorMessage = (err: unknown, fallback: string): string =>
  (err as AxiosError<{ error?: string }>)?.response?.data?.error || fallback;

export default function GeofenceManager({
  geofences = [],
  onChange,
  versioning = false,
  versionHistory = false,
  alertSwitch = false,
}: GeofenceManagerProps) {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('geofencing.manage');
  const [showForm, setShowForm] = useState(false);
  const [editingGeofence, setEditingGeofence] = useState<GeofenceRow | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [historyFor, setHistoryFor] = useState<GeofenceRow | null>(null);
  // ST-00282: the zone whose Alerts switch is being saved, and the last refusal.
  const [switching, setSwitching] = useState<number | null>(null);
  const [switchError, setSwitchError] = useState<string | null>(null);

  // ST-00282: the switch alone. Switching alerts on for a zone with no rules ticks On entry and
  // On exit — the server does it, as for the editor; the reloaded list shows the rules.
  const toggleAlerts = async (g: GeofenceRow, on: boolean) => {
    setSwitching(g.GeofenceID);
    setSwitchError(null);
    try {
      await api.geofences.update(g.GeofenceID, { alertsEnabled: on });
      onChange?.();
    } catch (err) {
      setSwitchError(
        `${g.Name}: ${errorMessage(err, `Could not switch its alerts ${on ? 'on' : 'off'}.`)}`
      );
    } finally {
      setSwitching(null);
    }
  };

  const handleCreate = async (data: GeofencePayload) => {
    try {
      await api.geofences.create(data);
      setShowForm(false);
      onChange?.();
    } catch (err) {
      console.error('Error creating geofence:', err);
      alert(errorMessage(err, 'Failed to create geofence'));
    }
  };

  const handleUpdate = async (data: GeofencePayload) => {
    if (!editingGeofence) return;
    try {
      await api.geofences.update(editingGeofence.GeofenceID, data);
      setEditingGeofence(null);
      onChange?.();
    } catch (err) {
      console.error('Error updating geofence:', err);
      alert(errorMessage(err, 'Failed to update geofence'));
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to deactivate this geofence?')) return;
    try {
      setDeleting(id);
      await api.geofences.delete(id);
      onChange?.();
    } catch (err) {
      console.error('Error deleting geofence:', err);
    } finally {
      setDeleting(null);
    }
  };

  if (canManage && (showForm || editingGeofence)) {
    return (
      <GeofenceForm
        geofence={editingGeofence}
        zones={geofences}
        versioning={versioning}
        versionHistory={versionHistory}
        alertSwitch={alertSwitch}
        onSubmit={editingGeofence ? handleUpdate : handleCreate}
        onCancel={() => {
          setShowForm(false);
          setEditingGeofence(null);
        }}
      />
    );
  }

  return (
    <div>
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Zones ({geofences.length})</h2>
        {canManage && (
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-colors"
          >
            <FaPlus size={12} />
            New Zone
          </button>
        )}
      </div>
      <RecomputeOffer
        objectType="geofence"
        objects={geofences}
        idKey="GeofenceID"
        versionKey="CurrentVersion"
        nameKey="Name"
      />
      {switchError && (
        <div
          role="alert"
          className="mb-3 flex items-center justify-between gap-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-2"
        >
          <span>{switchError}</span>
          <button type="button" onClick={() => setSwitchError(null)} className="text-xs font-semibold underline">
            Dismiss
          </button>
        </div>
      )}

      {/* Table */}
      {geofences.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-lg border border-gray-200">
          <FaMapMarkerAlt className="mx-auto text-gray-300 mb-3" size={40} />
          <p className="text-gray-500 text-sm">No zones created yet</p>
          {canManage && (
            <p className="text-gray-400 text-xs mt-1">
              Click "New Zone" to define your first zone boundary
            </p>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Radius
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Alerts
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dwell
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-surface divide-y divide-gray-200">
              {geofences.map((g) => {
                const alerts = versioning ? zoneRuleSummary(g.Rules) : null;
                const locked = zoneAlertsLocked(g.PurposeType);
                const switchOn = !alertSwitch || zoneAlertsOn(g);
                return (
                  <tr key={g.GeofenceID} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{g.Name}</p>
                        {g.description && (
                          <p className="text-xs text-gray-500 mt-0.5">{g.description}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {versioning && g.PurposeType ? (
                        <PurposeBadge
                          purpose={g.PurposeType}
                          loading={g.LoadingRole}
                          offloading={g.OffloadingRole}
                        />
                      ) : (
                        <ZoneTypeBadge type={getZoneType(g)} />
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {Number(g.Latitude).toFixed(5)}, {Number(g.Longitude).toFixed(5)}
                      {g.LinkedLocationName && (
                        <span className="block text-teal-600" title="Site">
                          {g.LinkedLocationName}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{g.RadiusMeters}m</td>
                    <td className="px-4 py-3 text-xs">
                      {versioning && alertSwitch && (
                        <div className="mb-1">
                          <ZoneAlertsSwitch
                            size="sm"
                            checked={switchOn}
                            locked={locked}
                            lockedReason={nogoAlertsReason(g.Schedule)}
                            disabled={!canManage || switching === g.GeofenceID}
                            onChange={canManage ? (on) => toggleAlerts(g, on) : undefined}
                            label={`Alerts for ${g.Name}`}
                          />
                        </div>
                      )}
                      {alerts ? (
                        <span className={switchOn ? '' : 'opacity-50'}>
                          <span
                            className={`inline-block px-1.5 py-0.5 rounded ${alerts.entry ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}
                          >
                            Entry
                          </span>{' '}
                          <span
                            className={`inline-block px-1.5 py-0.5 rounded ${alerts.exit ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}
                          >
                            Exit
                          </span>
                          {alerts.others > 0 && (
                            <span className="ml-1 text-gray-500">+{alerts.others}</span>
                          )}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {alerts?.dwellMinutes ? `${alerts.dwellMinutes} min` : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${
                          g.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {g.is_active ? 'Active' : 'Inactive'}
                      </span>
                      {versioning && g.CurrentVersion != null && (
                        <span className="ml-1.5 text-xs text-gray-400 tabular-nums">
                          v{g.CurrentVersion}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {versioning && versionHistory && (
                          <button
                            onClick={() => setHistoryFor(g)}
                            className="p-1.5 text-gray-400 hover:text-teal-600 transition-colors"
                            title="Version history"
                            aria-label={`Version history for ${g.Name}`}
                          >
                            <FaHistory size={14} />
                          </button>
                        )}
                        {canManage && (
                          <>
                            <button
                              onClick={() => setEditingGeofence(g)}
                              className="p-1.5 text-gray-400 hover:text-teal-600 transition-colors"
                              title="Edit"
                            >
                              <FaEdit size={14} />
                            </button>
                            <button
                              onClick={() => handleDelete(g.GeofenceID)}
                              disabled={deleting === g.GeofenceID}
                              className="p-1.5 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                              title="Deactivate"
                            >
                              <FaTrash size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {versioning && versionHistory && (
        <GeofenceVersionHistory
          geofence={historyFor}
          isOpen={!!historyFor}
          onClose={() => setHistoryFor(null)}
        />
      )}
    </div>
  );
}
